/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */

import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlinx.datetime.plus
import kotlinx.datetime.DatePeriod
import models.SavingsGoal
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import studio.DebtPayoffOrchestrator
import studio.DebtProjection
import studio.DebtScenarioKind
import studio.PaymentFrequency
import kotlin.js.Date as JsDate

/**
 * Debt Paydown Studio (web). Twin-plan redesign: two fully editable
 * scenarios (Plan A + Plan B), three payment frequencies, per-scenario
 * rate, and an "Apply to goal" button that commits a plan back to the
 * underlying SavingsGoal.
 *
 * The top of the Studio surfaces the underlying goal data inline (name,
 * starting balance, interest rate, original term). In edit mode those
 * inputs auto-save on blur via viewModel.updateGoal — they're factual
 * data about the debt, not exploratory state. In create mode they form
 * the draft that gets committed by the footer "Save" button.
 *
 * Layout: header → twin-plan strip → goal-data row → twin sliders → chart
 * (chart below sliders mirrors the Allocation Studio so the user sees
 * the slope change as they drag).
 */
class WebDebtStudioComponent(
    goal: SavingsGoal,
    private val viewModel: WebSavingsGoalsViewModel,
    private val onDismiss: () -> Unit,
    private val isCreating: Boolean = false,
    // When set, the Debt Studio is the Lab exploring an existing goal: goal-data
    // edits and plan picks stay in-draft (no auto-persist), and the footer is an
    // explicit "Apply to goal" that lists which fields change before committing.
    private val labApplyToGoal: SavingsGoal? = null,
) {
    private val labMode: Boolean get() = labApplyToGoal != null
    // Live goal — mutates as the user edits the inline goal-data row (in
    // edit mode that mutation happens on blur and persists via
    // viewModel.updateGoal; in create mode it's just draft state until
    // the user clicks Save in the footer).
    private var currentGoal: SavingsGoal = goal
    private var state: DebtPayoffOrchestrator.StudioState =
        DebtPayoffOrchestrator.defaultSliders(goal)
    private var backdrop: HTMLElement? = null
    private var appliedPlanKind: DebtScenarioKind? = null
    private var undoSnapshot: SavingsGoal? = null
    private var toastTimerId: Int? = null
    /** In create mode, which plan the user wants to commit on Save. Defaults
     *  to Plan A. Mirrors the vehicle checkbox pattern in the Allocation
     *  Studio so the user picks "which strategy" before saving. */
    private var selectedPlanInCreate: DebtScenarioKind = DebtScenarioKind.PLAN_A

    fun show() {
        val tm = WebTheme.current
        val bd = (document.createElement("div") as HTMLElement).apply {
            style.cssText = """
                position: fixed; inset: 0; background: rgba(0,0,0,0.6);
                z-index: 1200; display: flex; align-items: flex-start; justify-content: center;
                overflow-y: auto; padding: 24px 16px;
            """.trimIndent()
        }
        val panel = (document.createElement("div") as HTMLElement).apply {
            style.cssText = """
                background: ${tm.card}; color: ${tm.textPrimary};
                border-radius: 16px; width: 100%; max-width: 900px;
                padding: 24px; box-shadow: 0 24px 64px rgba(0,0,0,0.25);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                position: relative;
            """.trimIndent()
        }
        bd.appendChild(panel)
        document.body?.appendChild(bd)
        bd.addEventListener("click", { e -> if (e.target == bd) close() })
        panel.addEventListener("click", { it.stopPropagation() })
        backdrop = bd
        renderInto(panel)
    }

    private fun close() {
        backdrop?.remove()
        backdrop = null
        onDismiss()
    }

    private fun renderInto(panel: HTMLElement) {
        val tm = WebTheme.current
        // data-guide anchors mirror the Allocation Studio's pattern — each
        // section host carries a `data-guide` attribute and the in-modal
        // 'i' button (added in the header below) launches GuideScreen.STUDIO_DEBT.
        panel.innerHTML = """
            <div id="debtStudioHeader" data-guide="debt-studio-header" style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
                <h2 style="margin:0; font-size:20px; color:${tm.textPrimary};">
                    ${StringKey.DEBT_STUDIO_TITLE.tr()}
                </h2>
                <div style="display:flex; align-items:center; gap:12px;">
                    <button id="debtStudioGuide" title="Guide" style="
                        background:transparent; border:1px solid ${tm.border};
                        color:${tm.textSecondary}; width:28px; height:28px;
                        border-radius:50%; cursor:pointer; padding:0;
                        font-family:Georgia,'Times New Roman',serif;
                        font-style:italic; font-size:15px; font-weight:600;
                        line-height:1; display:inline-flex; align-items:center;
                        justify-content:center;
                    ">i</button>
                    <button id="debtStudioClose" style="
                        background:none; border:none; color:${tm.textSecondary};
                        font-size:24px; cursor:pointer; padding:0 4px; line-height:1;
                    ">&times;</button>
                </div>
            </div>
            <div id="debtStudioSubtitle" style="color:${tm.textSecondary}; font-size:13px; margin-bottom:18px;">
                ${escapeHtml(currentGoal.name)}
            </div>
            <div id="debtStudioStrip" data-guide="debt-studio-strip" style="display:flex; gap:12px; padding-bottom:8px;"></div>
            <div id="debtStudioGoalData" data-guide="debt-studio-goal-data" style="margin-top:16px;"></div>
            <div id="debtStudioLevers" data-guide="debt-studio-levers" style="margin-top:16px;"></div>
            <div id="debtStudioChart" data-guide="debt-studio-chart" style="margin-top:20px;"></div>
            <div style="margin-top:14px; font-size:11px; color:${tm.textSecondary}; line-height:1.45;">
                ⓘ ${StringKey.DEBT_STUDIO_PREPAY_NOTE.tr()}
            </div>
            <div style="margin-top:16px; padding-top:12px; border-top:1px solid ${tm.border};
                        color:${tm.textSecondary}; font-size:11px; line-height:1.5;">
                ${StringKey.STUDIO_DISCLAIMER.tr()}
            </div>
            <div id="debtStudioFooter" data-guide="debt-studio-footer" style="margin-top:18px;"></div>
            <div id="debtStudioToast" style="
                position:fixed; left:50%; bottom:24px; transform:translateX(-50%);
                background:#1f2235; color:#FFFFFF;
                padding:12px 18px; border-radius:10px; font-size:14px;
                display:none; align-items:center; gap:16px;
                box-shadow:0 6px 24px rgba(0,0,0,0.35);
                border:1px solid rgba(255,255,255,0.08);
                z-index:1300;
            "></div>
        """.trimIndent()
        panel.querySelector("#debtStudioClose")?.addEventListener("click", { close() })
        panel.querySelector("#debtStudioGuide")?.addEventListener("click", {
            GuideOverlay.ensureStylesInstalled()
            GuideOverlay(guide.GuideScreen.STUDIO_DEBT).start()
        })
        renderStrip(panel)
        renderGoalData(panel)
        renderLevers(panel)
        renderChart(panel)
        renderFooter(panel)
    }

    // -----------------------------------------------------------------------
    // Inline goal-data row (name / balance / rate / term). Auto-saves on
    // blur in edit mode; draft state only in create mode.
    // -----------------------------------------------------------------------

    private fun renderGoalData(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#debtStudioGoalData") as? HTMLElement ?: return
        val balance = currentGoal.debtRemainingBalance ?: 0.0
        val rate = currentGoal.debtInterestRate ?: 0.0
        val term = currentGoal.debtRemainingMonths ?: 0
        // The Lab is exploration-only. Priority is edited in the simple debt goal
        // form, not here — but its working value rides along to Save via
        // currentGoal (seeded from the goal at construction).
        host.innerHTML = """
            <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap:12px;">
                ${dataField("debtGoal-name", StringKey.DEBT_STUDIO_GOAL_NAME_LABEL.tr(), escapeHtml(currentGoal.name), "text", tm)}
                ${dataField("debtGoal-balance", StringKey.DEBT_STUDIO_GOAL_BALANCE_LABEL.tr(), formatBalanceForInput(balance), "number", tm, prefix = "$")}
                ${dataField("debtGoal-rate", StringKey.DEBT_STUDIO_GOAL_RATE_LABEL.tr(), formatRateForInput(rate), "number", tm)}
                ${dataField("debtGoal-term", StringKey.DEBT_STUDIO_GOAL_TERM_LABEL.tr(), if (term == 0) "" else term.toString(), "number", tm)}
            </div>
        """.trimIndent()

        // Wire each input — blur commits, in edit mode also persists.
        wireGoalDataInput(panel, "debtGoal-name") { raw ->
            val newName = raw.trim()
            currentGoal = currentGoal.copy(name = newName)
            (panel.querySelector("#debtStudioSubtitle") as? HTMLElement)?.textContent = newName
        }
        wireGoalDataInput(panel, "debtGoal-balance") { raw ->
            val parsed = raw.removePrefix("$").replace(",", "").trim().toDoubleOrNull() ?: 0.0
            currentGoal = currentGoal.copy(debtRemainingBalance = parsed)
            // Resync sliders so Plan A/B amortizing-payment defaults pick up
            // the new balance — keeps the projections meaningful.
            state = DebtPayoffOrchestrator.defaultSliders(currentGoal)
        }
        wireGoalDataInput(panel, "debtGoal-rate") { raw ->
            val parsed = raw.removeSuffix("%").trim().toDoubleOrNull() ?: 0.0
            currentGoal = currentGoal.copy(debtInterestRate = parsed)
            state = DebtPayoffOrchestrator.defaultSliders(currentGoal)
        }
        wireGoalDataInput(panel, "debtGoal-term") { raw ->
            val parsed = raw.trim().toIntOrNull()
            currentGoal = currentGoal.copy(debtRemainingMonths = parsed)
            state = DebtPayoffOrchestrator.defaultSliders(currentGoal)
        }
    }

    private fun dataField(
        id: String,
        label: String,
        value: String,
        type: String,
        tm: theme.ThemeColors,
        prefix: String? = null,
    ): String {
        val padLeft = if (prefix != null) "26px" else "10px"
        val prefixSpan = if (prefix != null) {
            """<span style="position:absolute; left:10px; top:50%; transform:translateY(-50%); color:${tm.textSecondary}; font-size:13px;">$prefix</span>"""
        } else ""
        return """
            <div>
                <label style="font-size:11px; color:${tm.textSecondary}; display:block; margin-bottom:4px;">$label</label>
                <div style="position:relative;">
                    $prefixSpan
                    <input id="$id" type="$type" value="$value"
                           style="width:100%; box-sizing:border-box;
                                  padding:8px 10px 8px $padLeft;
                                  border-radius:8px; border:1px solid ${tm.border};
                                  background:${tm.background}; color:${tm.textPrimary};
                                  font-size:14px; font-family:inherit;"/>
                </div>
            </div>
        """.trimIndent()
    }

    /** Wire blur + Enter for one inline goal-data input. The [apply] lambda
     *  must mutate [currentGoal]; this helper then persists in edit mode and
     *  re-runs projections. */
    private fun wireGoalDataInput(panel: HTMLElement, id: String, apply: (String) -> Unit) {
        val input = panel.querySelector("#$id") as? HTMLInputElement ?: return
        input.addEventListener("keydown", { evt ->
            val key = (evt as? org.w3c.dom.events.KeyboardEvent)?.key
            if (key == "Enter") {
                evt.preventDefault()
                input.blur()
            }
        })
        input.addEventListener("blur", { _ ->
            apply(input.value)
            if (!isCreating && !labMode) persistGoalDataEdit()
            renderStrip(panel)
            renderChart(panel)
            renderLevers(panel)
        })
    }

    /** Persist the current goal-data row state via viewModel.updateGoal.
     *  Only called in edit mode (in create mode the draft commits via the
     *  footer Save button). */
    private fun persistGoalDataEdit() {
        val deadlineJs = JsDate("${currentGoal.deadline.year}-${currentGoal.deadline.monthNumber.toString().padStart(2, '0')}-${currentGoal.deadline.dayOfMonth.toString().padStart(2, '0')}")
        viewModel.updateGoal(
            goal = currentGoal,
            name = currentGoal.name,
            targetAmount = currentGoal.targetAmount,
            deadline = deadlineJs,
            category = currentGoal.category,
            priority = currentGoal.priority,
            accountType = currentGoal.accountType,
            debtInterestRate = currentGoal.debtInterestRate,
            debtRemainingBalance = currentGoal.debtRemainingBalance,
            debtRemainingMonths = currentGoal.debtRemainingMonths,
            debtPaymentFrequency = currentGoal.debtPaymentFrequency,
            debtMonthlyEquivalentPayment = currentGoal.debtMonthlyEquivalentPayment,
        )
    }

    private fun formatBalanceForInput(v: Double): String =
        if (v == 0.0) "" else v.toLong().toString()

    private fun formatRateForInput(v: Double): String =
        if (v == 0.0) "" else (kotlin.math.round(v * 100) / 100).toString()

    // -----------------------------------------------------------------------
    // Twin plan cards (strip)
    // -----------------------------------------------------------------------

    private fun renderStrip(panel: HTMLElement) {
        val tm = WebTheme.current
        val strip = panel.querySelector("#debtStudioStrip") as? HTMLElement ?: return
        val projections = DebtPayoffOrchestrator.runComparison(currentGoal, state)
        strip.innerHTML = projections.joinToString("") { renderPlanCard(it, projections, tm) }

        // Helper: pick a plan. In create mode this just updates the in-memory
        // selection (committed when the user clicks Save). In edit mode this
        // is the apply action — clicking a plan's checkbox immediately
        // persists that plan's monthly + frequency back to the goal.
        fun selectPlan(kind: DebtScenarioKind) {
            if (isCreating || labMode) {
                // Draft-only pick; committed together by the footer ("Save" in
                // create, "Apply to goal" in the Lab).
                selectedPlanInCreate = kind
                renderStrip(panel)
            } else {
                val proj = projections.firstOrNull { it.scenario.kind == kind } ?: return
                applyToGoal(panel, proj)
            }
        }

        // Wire plan-selection checkboxes in BOTH modes. Radio-like: selecting
        // one plan deselects the other (in create mode by re-rendering; in
        // edit mode by re-running applyToGoal which sets appliedPlanKind).
        for (proj in projections) {
            val kind = proj.scenario.kind
            panel.querySelector("#debt-plan-cb-${kind.name}")?.addEventListener("change", {
                selectPlan(kind)
            })
        }
        // Whole-card click — same as ticking the checkbox. Skip when the
        // user actually clicked the checkbox so we don't double-toggle.
        panel.querySelectorAll(".debt-plan-card").let { nodes ->
            for (i in 0 until nodes.length) {
                val card = nodes.item(i) as? HTMLElement ?: continue
                card.addEventListener("click", { ev ->
                    val target = ev.target as? org.w3c.dom.Element
                    if (target != null && target.tagName.lowercase() == "input") return@addEventListener
                    val k = card.getAttribute("data-plan") ?: return@addEventListener
                    val kind = runCatching { DebtScenarioKind.valueOf(k) }.getOrNull() ?: return@addEventListener
                    selectPlan(kind)
                })
            }
        }
    }

    private fun renderPlanCard(p: DebtProjection, all: List<DebtProjection>, tm: theme.ThemeColors): String {
        val label = when (p.scenario.kind) {
            DebtScenarioKind.PLAN_A -> StringKey.DEBT_STUDIO_PLAN_A.tr()
            DebtScenarioKind.PLAN_B -> StringKey.DEBT_STUDIO_PLAN_B.tr()
        }
        val accent = scenarioAccent(p.scenario.kind, tm)
        val warning = if (p.negativeAmortization) """
            <div style="font-size:11px; color:${tm.expenseRed}; margin-top:6px; line-height:1.35;">
                ⚠ ${StringKey.DEBT_STUDIO_NEGATIVE_AMORT_WARNING.tr()}
            </div>
        """.trimIndent() else ""
        // Delta tag relative to the other plan
        val other = all.firstOrNull { it.scenario.kind != p.scenario.kind }
        val delta = if (other != null && !p.negativeAmortization && !other.negativeAmortization) {
            val interestSaved = other.totalInterestPaid - p.totalInterestPaid
            val monthsSaved = other.monthsToPayoff - p.monthsToPayoff
            val parts = mutableListOf<String>()
            if (interestSaved > 0.0) parts += StringKey.DEBT_STUDIO_DELTA_SAVES.tr("\$${formatMoney(interestSaved)}")
            if (monthsSaved > 0) parts += StringKey.DEBT_STUDIO_DELTA_FINISHES_SOONER.tr(formatMonthsAsYearsMonths(monthsSaved))
            if (parts.isNotEmpty()) """
                <div style="font-size:11px; color:${tm.incomeGreen}; margin-top:6px; line-height:1.35;">
                    ${parts.joinToString(" · ")}
                </div>
            """.trimIndent() else ""
        } else ""
        // Selected = "this is the user's chosen strategy". In create mode that
        // means "will be persisted on Save"; in edit mode it means "currently
        // applied to the goal" (the source of truth is appliedPlanKind, which
        // applyToGoal() updates on every commit). Same visual treatment for
        // both modes — a checkbox + frame; clicking the checkbox (or the card)
        // either updates selection or persists, depending on mode.
        val isSelected = if (isCreating || labMode) selectedPlanInCreate == p.scenario.kind
                         else appliedPlanKind == p.scenario.kind
        val cardBorderColor = if (isSelected) accent else tm.border
        val cardBorderWidth = if (isSelected) "2" else "1"
        val checkedAttr = if (isSelected) "checked" else ""
        val tooltip = StringKey.DEBT_STUDIO_PLAN_TOOLTIP.tr()
        val headerLeading = """
            <input type="checkbox" id="debt-plan-cb-${p.scenario.kind.name}"
                   class="debt-plan-cb" data-plan="${p.scenario.kind.name}"
                   $checkedAttr
                   title="${escapeHtml(tooltip)}"
                   style="accent-color:$accent; width:16px; height:16px; cursor:pointer;"/>
        """.trimIndent()
        return """
            <div class="debt-plan-card"
                 data-plan="${p.scenario.kind.name}"
                 title="${escapeHtml(tooltip)}"
                 style="
                flex:1 1 0; padding:16px; border-radius:12px;
                background:${tm.background};
                border:${cardBorderWidth}px solid $cardBorderColor;
                display:flex; flex-direction:column; gap:8px;
                cursor:pointer;
            ">
                <div style="display:flex; align-items:center; gap:8px;">
                    $headerLeading
                    <div style="font-size:14px; font-weight:600; color:${tm.textPrimary};">$label</div>
                </div>
                <div style="display:flex; justify-content:space-between; gap:8px; margin-top:4px;">
                    <div>
                        <div style="font-size:10px; color:${tm.textSecondary}; text-transform:uppercase; letter-spacing:0.5px;">
                            ${StringKey.DEBT_STUDIO_STAT_PAYOFF.tr()}
                        </div>
                        <div style="font-size:18px; font-weight:700; color:${tm.textPrimary};">
                            ${if (p.negativeAmortization) "∞" else formatMonthsAsYearsMonths(p.monthsToPayoff)}
                        </div>
                    </div>
                    <div>
                        <div style="font-size:10px; color:${tm.textSecondary}; text-transform:uppercase; letter-spacing:0.5px;">
                            ${StringKey.DEBT_STUDIO_STAT_INTEREST.tr()}
                        </div>
                        <div style="font-size:18px; font-weight:700; color:${tm.textPrimary};">
                            ${'$'}${formatMoney(p.totalInterestPaid)}
                        </div>
                    </div>
                </div>
                <div>
                    <div style="font-size:10px; color:${tm.textSecondary}; text-transform:uppercase; letter-spacing:0.5px;">
                        ${StringKey.DEBT_STUDIO_STAT_TOTAL.tr()}
                    </div>
                    <div style="font-size:14px; font-weight:600; color:${tm.textPrimary};">
                        ${'$'}${formatMoney(p.totalCost)}
                    </div>
                </div>
                $warning
                $delta
            </div>
        """.trimIndent()
    }

    /** Cyan for Plan A, amber for Plan B — matches the chart lines. */
    private fun scenarioAccent(kind: DebtScenarioKind, tm: theme.ThemeColors): String = when (kind) {
        DebtScenarioKind.PLAN_A -> tm.accent
        DebtScenarioKind.PLAN_B -> "#F5A623"
    }

    // -----------------------------------------------------------------------
    // Footer (Save / Cancel + Delete)
    // -----------------------------------------------------------------------

    private fun renderFooter(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#debtStudioFooter") as? HTMLElement ?: return
        if (isCreating) {
            // Create mode: Save commits the draft via viewModel.createGoal,
            // Cancel discards. No Delete (nothing to delete yet).
            host.innerHTML = """
                <div style="display:flex; justify-content:flex-end; gap:10px; align-items:center;">
                    <button id="debtStudio-cancel" style="
                        background:transparent; color:${tm.textPrimary};
                        border:1px solid ${tm.border}; border-radius:8px;
                        padding:8px 14px; font-size:13px; font-weight:600;
                        cursor:pointer; font-family:inherit;
                    ">${StringKey.STUDIO_CANCEL_ACTION.tr()}</button>
                    <button id="debtStudio-save" style="
                        background:${tm.accent}; color:white;
                        border:none; border-radius:8px;
                        padding:8px 18px; font-size:13px; font-weight:600;
                        cursor:pointer; font-family:inherit;
                    ">${StringKey.STUDIO_SAVE_ACTION.tr()}</button>
                </div>
            """.trimIndent()
            host.querySelector("#debtStudio-cancel")?.addEventListener("click", { close() })
            host.querySelector("#debtStudio-save")?.addEventListener("click", { saveCreate() })
        } else {
            // Edit mode. In the Lab, an explicit "Apply to goal" commits the
            // draft (goal-data + chosen plan) with a field-diff confirm. In the
            // plain edit surface, goal-data auto-saves on blur and per-plan
            // "Apply to goal" is the commit point, so there's just Delete here.
            val applyButton = if (labMode) """
                <button id="debtStudio-apply" style="
                    background:${tm.accent}; color:white; border:none; border-radius:8px;
                    padding:8px 18px; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit;
                ">${StringKey.LAB_APPLY_TO_GOAL.tr()}</button>
            """.trimIndent() else "<span></span>"
            host.innerHTML = """
                <div style="display:flex; justify-content:space-between; gap:10px; align-items:center;">
                    <button id="debtStudio-delete" style="
                        background:transparent; color:${tm.expenseRed};
                        border:1px solid ${tm.expenseRed}; border-radius:8px;
                        padding:8px 14px; font-size:13px; font-weight:600;
                        cursor:pointer; font-family:inherit;
                    ">${StringKey.STUDIO_DELETE_GOAL.tr()}</button>
                    $applyButton
                </div>
            """.trimIndent()
            host.querySelector("#debtStudio-delete")?.addEventListener("click", {
                viewModel.deleteGoal(currentGoal.id)
                close()
            })
            host.querySelector("#debtStudio-apply")?.addEventListener("click", { showDebtApplyToGoalConfirm() })
        }
    }

    /** "Apply to goal" for the debt Lab — list which fields change vs the
     *  original goal (balance, rate, term, payoff date), and commit only on
     *  confirm. Mirrors the savings Lab's Apply-to-goal. */
    private fun showDebtApplyToGoalConfirm() {
        val orig = labApplyToGoal ?: return
        val tm = WebTheme.current
        val projections = DebtPayoffOrchestrator.runComparison(currentGoal, state)
        val selectedProjection = projections.firstOrNull { it.scenario.kind == selectedPlanInCreate } ?: projections.firstOrNull()
        val selectedScenario = selectedProjection?.scenario
        val planRatePercent = selectedScenario?.let { it.annualRate * 100.0 } ?: currentGoal.debtInterestRate
        val planMonths = selectedProjection?.monthsToPayoff?.takeIf { it > 0 } ?: currentGoal.debtRemainingMonths
        val today = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
        val deadlineLocal = if (planMonths != null && planMonths > 0) today.plus(kotlinx.datetime.DatePeriod(months = planMonths)) else currentGoal.deadline

        fun money(v: Double?): String = if (v == null) "—" else "$" + kotlin.math.round(v).toInt().toString()
        fun pct(v: Double?): String = if (v == null) "—" else "${(kotlin.math.round((v) * 100) / 100)}%"
        fun months(v: Int?): String = v?.let { "$it mo" } ?: "—"
        fun dateStr(d: kotlinx.datetime.LocalDate): String = "${d.year}-${d.monthNumber.toString().padStart(2, '0')}"

        val fields = listOf(
            listOf(StringKey.DEBT_STUDIO_GOAL_BALANCE_LABEL.tr(), money(orig.debtRemainingBalance), money(currentGoal.debtRemainingBalance), (orig.debtRemainingBalance != currentGoal.debtRemainingBalance).toString()),
            listOf(StringKey.DEBT_STUDIO_GOAL_RATE_LABEL.tr(), pct(orig.debtInterestRate), pct(planRatePercent), ((orig.debtInterestRate ?: 0.0) != (planRatePercent ?: 0.0)).toString()),
            listOf(StringKey.DEBT_STUDIO_GOAL_TERM_LABEL.tr(), months(orig.debtRemainingMonths), months(planMonths), (orig.debtRemainingMonths != planMonths).toString()),
            listOf(StringKey.LAB_FIELD_DEADLINE.tr(), dateStr(orig.deadline), dateStr(deadlineLocal), (orig.deadline != deadlineLocal).toString()),
        )
        val anyChanged = fields.any { it[3] == "true" }
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = "position:fixed; inset:0; background:rgba(0,0,0,0.5); z-index:1300; display:flex; align-items:center; justify-content:center;"
        val rowsHtml = fields.joinToString("") { f ->
            val changed = f[3] == "true"
            val valueHtml = if (changed)
                """<span style="color:${tm.textSecondary};">${f[1]}</span> <span style="color:${tm.textSecondary};">→</span> <span style="color:${tm.accent}; font-weight:700;">${f[2]}</span>"""
            else """<span style="color:${tm.textSecondary};">${f[2]}</span>"""
            """<div style="display:flex; justify-content:space-between; gap:12px; align-items:center; padding:9px 0; border-bottom:1px solid ${tm.border}; opacity:${if (changed) "1" else "0.55"};">
                <span style="color:${tm.textPrimary}; font-size:0.85rem; font-weight:${if (changed) "600" else "500"};">${f[0]}</span>
                <span style="font-size:0.85rem; text-align:right;">$valueHtml</span>
            </div>"""
        }
        val noteHtml = if (!anyChanged) """<div style="color:${tm.textSecondary}; font-size:0.82rem; margin-top:0.75rem;">${StringKey.LAB_APPLY_NO_CHANGES.tr()}</div>""" else ""
        overlay.innerHTML = """
            <div style="background:${tm.card}; border:1px solid ${tm.border}; border-radius:14px; padding:1.5rem; max-width:420px; width:90%;">
                <h3 style="margin:0 0 1rem 0; color:${tm.textPrimary}; font-size:1.1rem;">${StringKey.LAB_APPLY_TITLE.tr()}</h3>
                <div style="margin-bottom:1.25rem;">$rowsHtml$noteHtml</div>
                <div style="display:flex; justify-content:flex-end; gap:10px;">
                    <button id="debtApplyCancel" style="background:transparent; color:${tm.textPrimary}; border:1px solid ${tm.border}; border-radius:8px; padding:8px 14px; font-weight:600; cursor:pointer;">${StringKey.ACTION_CANCEL.tr()}</button>
                    <button id="debtApplyConfirm" ${if (!anyChanged) "disabled" else ""} style="background:${if (!anyChanged) tm.border else tm.accent}; color:${if (!anyChanged) tm.textSecondary else "white"}; border:none; border-radius:8px; padding:8px 18px; font-weight:600; cursor:${if (!anyChanged) "not-allowed" else "pointer"};">${StringKey.LAB_APPLY_CONFIRM.tr()}</button>
                </div>
            </div>
        """.trimIndent()
        document.body?.appendChild(overlay)
        val closeOverlay = { overlay.remove() }
        overlay.querySelector("#debtApplyCancel")?.addEventListener("click", { closeOverlay() })
        overlay.addEventListener("click", { e -> if (e.target == overlay) closeOverlay() })
        overlay.querySelector("#debtApplyConfirm")?.addEventListener("click", {
            if (anyChanged) {
                val planMonthlyEquiv = selectedScenario?.monthlyPayment ?: currentGoal.debtMonthlyEquivalentPayment
                val planFrequency = selectedScenario?.paymentFrequency ?: currentGoal.debtPaymentFrequency
                val deadlineJs = JsDate("${deadlineLocal.year}-${deadlineLocal.monthNumber.toString().padStart(2, '0')}-${deadlineLocal.dayOfMonth.toString().padStart(2, '0')}")
                viewModel.updateGoal(
                    goal = orig, name = currentGoal.name, targetAmount = currentGoal.targetAmount, deadline = deadlineJs,
                    category = currentGoal.category, priority = currentGoal.priority, iconEmoji = currentGoal.iconEmoji ?: "tax",
                    accountType = currentGoal.accountType,
                    debtInterestRate = planRatePercent, debtRemainingBalance = currentGoal.debtRemainingBalance,
                    debtRemainingMonths = planMonths, debtPaymentFrequency = planFrequency, debtMonthlyEquivalentPayment = planMonthlyEquiv,
                )
                closeOverlay()
                close()
            }
        })
    }

    private fun saveCreate() {
        if (currentGoal.name.isBlank()) {
            showAppAlert("Please enter a goal name")
            return
        }
        // Commit the user's selected plan as the goal's strategy. This mirrors
        // what "Apply to goal" does in edit mode: persists rate (×100 back to
        // % storage), monthly-equivalent payment, payment frequency, term, and
        // pushes the deadline out to payoff date.
        val projections = DebtPayoffOrchestrator.runComparison(currentGoal, state)
        val selectedProjection = projections.firstOrNull { it.scenario.kind == selectedPlanInCreate }
            ?: projections.firstOrNull()
        val selectedScenario = selectedProjection?.scenario
        val planRatePercent = selectedScenario?.let { it.annualRate * 100.0 } ?: currentGoal.debtInterestRate
        val planMonthlyEquiv = selectedScenario?.monthlyPayment ?: currentGoal.debtMonthlyEquivalentPayment
        val planFrequency = selectedScenario?.paymentFrequency ?: currentGoal.debtPaymentFrequency
        val planMonths = selectedProjection?.monthsToPayoff?.takeIf { it > 0 } ?: currentGoal.debtRemainingMonths
        val today = kotlinx.datetime.Clock.System.todayIn(kotlinx.datetime.TimeZone.currentSystemDefault())
        val deadlineLocal = if (planMonths != null && planMonths > 0)
            today.plus(kotlinx.datetime.DatePeriod(months = planMonths))
        else
            currentGoal.deadline
        val deadlineJs = JsDate("${deadlineLocal.year}-${deadlineLocal.monthNumber.toString().padStart(2, '0')}-${deadlineLocal.dayOfMonth.toString().padStart(2, '0')}")
        viewModel.createGoal(
            name = currentGoal.name,
            category = currentGoal.category,
            targetAmount = currentGoal.targetAmount,
            deadline = deadlineJs,
            iconEmoji = currentGoal.iconEmoji ?: "tax",
            priority = currentGoal.priority,
            accountType = currentGoal.accountType,
            onCreated = { created ->
                // Persist the debt fields + the selected plan's strategy on
                // the freshly-created goal. createGoal() doesn't take those
                // fields, so we follow up with an update.
                val createdDeadlineJs = JsDate("${created.deadline.year}-${created.deadline.monthNumber.toString().padStart(2, '0')}-${created.deadline.dayOfMonth.toString().padStart(2, '0')}")
                viewModel.updateGoal(
                    goal = created,
                    name = created.name,
                    targetAmount = created.targetAmount,
                    deadline = createdDeadlineJs,
                    category = created.category,
                    priority = currentGoal.priority,
                    accountType = created.accountType,
                    debtInterestRate = planRatePercent,
                    debtRemainingBalance = currentGoal.debtRemainingBalance,
                    debtRemainingMonths = planMonths,
                    debtPaymentFrequency = planFrequency,
                    debtMonthlyEquivalentPayment = planMonthlyEquiv,
                )
                close()
            },
        )
    }

    // -----------------------------------------------------------------------
    // Chart
    // -----------------------------------------------------------------------

    private fun renderChart(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#debtStudioChart") as? HTMLElement ?: return
        val projections = DebtPayoffOrchestrator.runComparison(currentGoal, state)
        if (projections.isEmpty() || projections.all { it.points.size <= 1 }) {
            host.innerHTML = ""
            return
        }
        // Narrow viewports scale the SVG down hard (~360/840 ≈ 0.43x), making
        // an SVG-unit font-size of 11 render at ~4.7px — invisible. Bump tick
        // font sizes + padB on mobile only; desktop layout is unchanged.
        val isMobile = kotlinx.browser.window.innerWidth < 720
        val width = 840
        val height = if (isMobile) 320 else 280
        val padL = 56
        val padR = 16
        val padT = 16
        val padB = if (isMobile) 56 else 32
        val xTickFontSize = if (isMobile) 22 else 11
        val yTickFontSize = if (isMobile) 22 else 12
        val xTickBaseline = if (isMobile) 28 else 18
        val plotW = width - padL - padR
        val plotH = height - padT - padB

        val maxBalance = projections.maxOf { p -> p.points.maxOf { it.balance } }.coerceAtLeast(1.0) * 1.1
        val maxMonth = projections.maxOf { p -> p.points.maxOf { it.month } }.coerceAtLeast(1)

        fun xFor(month: Int): Double = padL + (month.toDouble() / maxMonth) * plotW
        fun yFor(amount: Double): Double = padT + plotH - (amount / maxBalance) * plotH

        val gridlines = buildString {
            for (i in 0..4) {
                val y = padT + plotH - (i / 4.0) * plotH
                val tickVal = (maxBalance * i / 4.0).toLong()
                append("""<line x1="$padL" y1="$y" x2="${padL + plotW}" y2="$y" stroke="${tm.border}" stroke-width="1" stroke-dasharray="2,3"/>""")
                append("""<text x="${padL - 8}" y="${y + 4}" font-size="$yTickFontSize" font-weight="600" fill="${tm.textPrimary}" text-anchor="end">${'$'}${formatShort(tickVal.toDouble())}</text>""")
            }
        }
        val years = (maxMonth / 12.0).toInt().coerceAtLeast(1)
        val xTicks = buildString {
            for (i in 0..years) {
                val x = xFor((i * 12).coerceAtMost(maxMonth))
                append("""<text x="$x" y="${padT + plotH + xTickBaseline}" font-size="$xTickFontSize" fill="${tm.textSecondary}" text-anchor="middle">+${i}y</text>""")
            }
        }
        val lines = buildString {
            for (p in projections) {
                val accent = scenarioAccent(p.scenario.kind, tm)
                val pts = p.points
                    .joinToString(" ") { pt -> "${xFor(pt.month)},${yFor(pt.balance)}" }
                append("""<polyline points="$pts" fill="none" stroke="$accent" stroke-width="2.5"/>""")
                val last = p.points.last()
                append("""<circle cx="${xFor(last.month)}" cy="${yFor(last.balance)}" r="4" fill="$accent"/>""")
            }
        }
        host.innerHTML = """
            <div style="background:${tm.background}; border-radius:12px; padding:16px;">
                <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
                    <div style="font-size:13px; font-weight:600; color:${tm.textPrimary};">
                        ${StringKey.DEBT_STUDIO_CHART_TITLE.tr()}
                    </div>
                    <div style="font-size:11px; color:${tm.textSecondary};">
                        ${StringKey.DEBT_STUDIO_CHART_HINT.tr()}
                    </div>
                </div>
                <svg viewBox="0 0 $width $height" preserveAspectRatio="xMidYMid meet" style="width:100%; height:auto; display:block;">
                    $gridlines
                    $xTicks
                    $lines
                </svg>
            </div>
        """.trimIndent()
    }

    // -----------------------------------------------------------------------
    // Twin slider rows — one per plan
    // -----------------------------------------------------------------------

    private fun renderLevers(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#debtStudioLevers") as? HTMLElement ?: return
        host.innerHTML = """
            ${planLeverRow(DebtScenarioKind.PLAN_A, state.planA, tm)}
            ${planLeverRow(DebtScenarioKind.PLAN_B, state.planB, tm)}
        """.trimIndent()
        wirePlanRow(panel, DebtScenarioKind.PLAN_A)
        wirePlanRow(panel, DebtScenarioKind.PLAN_B)
    }

    private fun planLeverRow(kind: DebtScenarioKind, slider: DebtPayoffOrchestrator.SliderState, tm: theme.ThemeColors): String {
        val balance = currentGoal.debtRemainingBalance ?: 0.0
        val paymentMax = (balance / 6).coerceAtLeast(1_000.0).coerceAtMost(20_000.0)
        val lumpSumMax = balance.coerceAtLeast(1_000.0)
        val label = when (kind) {
            DebtScenarioKind.PLAN_A -> StringKey.DEBT_STUDIO_PLAN_A.tr()
            DebtScenarioKind.PLAN_B -> StringKey.DEBT_STUDIO_PLAN_B.tr()
        }
        val accent = scenarioAccent(kind, tm)
        val paymentLabel = when (slider.paymentFrequency) {
            PaymentFrequency.MONTHLY -> StringKey.DEBT_STUDIO_LEVER_PAYMENT_MONTHLY.tr()
            PaymentFrequency.SEMI_MONTHLY -> StringKey.DEBT_STUDIO_LEVER_PAYMENT_SEMI_MONTHLY.tr()
            PaymentFrequency.ACCELERATED_BIWEEKLY -> StringKey.DEBT_STUDIO_LEVER_PAYMENT_ACCEL_BIWEEKLY.tr()
        }
        val displayedPayment = slider.paymentFrequency.periodPayment(slider.monthlyPayment)
        return """
            <div style="margin-top:18px;">
                <div style="display:flex; align-items:center; gap:12px; margin-bottom:8px;">
                    <span style="display:inline-block; width:8px; height:8px; background:$accent; border-radius:2px;"></span>
                    <strong style="font-size:13px; color:${tm.textPrimary}; letter-spacing:0.3px;">$label</strong>
                    ${freqPicker(kind, slider.paymentFrequency, tm)}
                </div>
                <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap:18px;">
                    ${slider(
                        id = "debt-${kind.name}-rate",
                        label = StringKey.DEBT_STUDIO_LEVER_RATE.tr(),
                        min = 0.0, max = 0.10, step = 0.0005,
                        value = slider.annualRate,
                        formatter = "percent",
                        tm = tm,
                    )}
                    ${slider(
                        id = "debt-${kind.name}-payment",
                        label = paymentLabel,
                        min = 0.0, max = paymentMax, step = 1.0,
                        value = displayedPayment,
                        formatter = "money",
                        tm = tm,
                    )}
                    ${slider(
                        id = "debt-${kind.name}-lumpsum",
                        label = StringKey.DEBT_STUDIO_LEVER_LUMPSUM.tr(),
                        min = 0.0, max = lumpSumMax, step = 1.0,
                        value = slider.lumpSum,
                        formatter = "money",
                        tm = tm,
                    )}
                </div>
            </div>
        """.trimIndent()
    }

    private fun freqPicker(kind: DebtScenarioKind, current: PaymentFrequency, tm: theme.ThemeColors): String {
        fun btn(freq: PaymentFrequency, label: String): String {
            val selected = freq == current
            val bg = if (selected) tm.accent else "transparent"
            val fg = if (selected) "#0a0a0a" else tm.textSecondary
            val border = if (selected) tm.accent else tm.border
            return """
                <button type="button" data-freq="${freq.name}" id="debt-${kind.name}-freq-${freq.name}" style="
                    background:$bg; color:$fg; border:1px solid $border;
                    padding:4px 10px; font-size:11px; font-weight:600;
                    border-radius:6px; cursor:pointer; line-height:1.3;
                ">$label</button>
            """.trimIndent()
        }
        return """
            <div style="display:flex; gap:4px; margin-left:auto;">
                ${btn(PaymentFrequency.MONTHLY, StringKey.DEBT_STUDIO_FREQ_MONTHLY.tr())}
                ${btn(PaymentFrequency.SEMI_MONTHLY, StringKey.DEBT_STUDIO_FREQ_SEMI_MONTHLY.tr())}
                ${btn(PaymentFrequency.ACCELERATED_BIWEEKLY, StringKey.DEBT_STUDIO_FREQ_ACCEL_BIWEEKLY.tr())}
            </div>
        """.trimIndent()
    }

    private fun wirePlanRow(panel: HTMLElement, kind: DebtScenarioKind) {
        val rateId = "debt-${kind.name}-rate"
        val paymentId = "debt-${kind.name}-payment"
        val lumpsumId = "debt-${kind.name}-lumpsum"

        wireSlider(panel, rateId) { v -> mutate(panel, kind) { it.copy(annualRate = v) } }
        wireSlider(panel, paymentId) { v ->
            // Displayed value is per-period dollars for the active frequency.
            // Convert back to monthly-equivalent for storage.
            val freq = sliderFor(kind).paymentFrequency
            val monthlyEquiv = when (freq) {
                PaymentFrequency.MONTHLY -> v
                PaymentFrequency.SEMI_MONTHLY -> v * 2.0
                PaymentFrequency.ACCELERATED_BIWEEKLY -> v * 2.0
            }
            mutate(panel, kind) { it.copy(monthlyPayment = monthlyEquiv) }
        }
        wireSlider(panel, lumpsumId) { v -> mutate(panel, kind) { it.copy(lumpSum = v) } }

        // Frequency picker buttons
        for (freq in PaymentFrequency.entries) {
            panel.querySelector("#debt-${kind.name}-freq-${freq.name}")?.addEventListener("click", {
                mutate(panel, kind) { it.copy(paymentFrequency = freq) }
                // Re-render levers so payment slider's label + displayed value reflect the new cadence.
                renderLevers(panel)
            })
        }
    }

    private fun sliderFor(kind: DebtScenarioKind): DebtPayoffOrchestrator.SliderState = when (kind) {
        DebtScenarioKind.PLAN_A -> state.planA
        DebtScenarioKind.PLAN_B -> state.planB
    }

    private fun mutate(
        panel: HTMLElement,
        kind: DebtScenarioKind,
        f: (DebtPayoffOrchestrator.SliderState) -> DebtPayoffOrchestrator.SliderState,
    ) {
        state = when (kind) {
            DebtScenarioKind.PLAN_A -> state.copy(planA = f(state.planA))
            DebtScenarioKind.PLAN_B -> state.copy(planB = f(state.planB))
        }
        // Editing a slider on the applied plan invalidates the "Applied" chip.
        if (appliedPlanKind == kind) appliedPlanKind = null
        renderStrip(panel)
        renderChart(panel)
    }

    private fun slider(
        id: String,
        label: String,
        min: Double,
        max: Double,
        step: Double,
        value: Double,
        formatter: String,
        tm: theme.ThemeColors,
    ): String {
        val display = formatSliderValue(value, formatter)
        return """
            <div>
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; gap:8px;">
                    <label style="font-size:12px; color:${tm.textSecondary};">$label</label>
                    <input id="$id-display" type="text" value="$display"
                           style="font-size:13px; font-weight:600; color:${tm.textPrimary};
                                  background:transparent; border:1px solid transparent;
                                  border-radius:6px; padding:2px 6px; min-width:60px;
                                  text-align:right; cursor:text; font-family:inherit;
                                  outline:none; transition:border-color 0.15s;"
                           onfocus="this.style.borderColor='${tm.accent}'; this.select();"
                           onblur="this.style.borderColor='transparent'"/>
                </div>
                <input type="range" id="$id" min="$min" max="$max" step="$step" value="$value"
                       data-formatter="$formatter"
                       style="width:100%; accent-color:${tm.accent};"/>
            </div>
        """.trimIndent()
    }

    private fun wireSlider(panel: HTMLElement, id: String, update: (Double) -> Unit) {
        val input = panel.querySelector("#$id") as? HTMLInputElement ?: return
        val display = panel.querySelector("#$id-display") as? HTMLInputElement
        val formatter = input.getAttribute("data-formatter") ?: "money"
        input.addEventListener("input", { _ ->
            val v = input.value.toDoubleOrNull() ?: return@addEventListener
            update(v)
            display?.value = formatSliderValue(v, formatter)
            renderStrip(panel)
            renderChart(panel)
        })
        display?.addEventListener("keydown", { evt ->
            val key = (evt as? org.w3c.dom.events.KeyboardEvent)?.key
            if (key == "Enter") {
                evt.preventDefault()
                display.blur()
            } else if (key == "Escape") {
                display.value = formatSliderValue(input.value.toDoubleOrNull() ?: 0.0, formatter)
                display.blur()
            }
        })
        display?.addEventListener("blur", { _ ->
            val parsed = parseSliderValue(display.value, formatter)
            if (parsed != null) {
                val min = input.getAttribute("min")?.toDoubleOrNull() ?: 0.0
                val max = input.getAttribute("max")?.toDoubleOrNull() ?: 0.0
                val clamped = parsed.coerceIn(min, max)
                input.value = clamped.toString()
                update(clamped)
                renderStrip(panel)
                renderChart(panel)
            }
            display.value = formatSliderValue(input.value.toDoubleOrNull() ?: 0.0, formatter)
        })
    }

    // -----------------------------------------------------------------------
    // Apply to goal + undo toast
    // -----------------------------------------------------------------------

    private fun applyToGoal(panel: HTMLElement, projection: DebtProjection) {
        // Snapshot current goal debt fields so undo can roll back.
        undoSnapshot = currentGoal.copy()

        val today = todayLocalDate()
        val months = projection.monthsToPayoff.coerceAtLeast(0)
        val newDeadline = today.plus(DatePeriod(months = months))
        val newDeadlineJs = JsDate("${newDeadline.year}-${newDeadline.monthNumber.toString().padStart(2, '0')}-${newDeadline.dayOfMonth.toString().padStart(2, '0')}")

        viewModel.updateGoal(
            goal = currentGoal,
            name = currentGoal.name,
            targetAmount = currentGoal.targetAmount,
            deadline = newDeadlineJs,
            priority = currentGoal.priority,
            debtInterestRate = projection.scenario.annualRate * 100.0,
            debtRemainingBalance = currentGoal.debtRemainingBalance,
            debtRemainingMonths = months,
            debtPaymentFrequency = projection.scenario.paymentFrequency,
            debtMonthlyEquivalentPayment = projection.scenario.monthlyPayment,
        )
        // Mirror the applied plan into the local goal so subsequent edits
        // (e.g. another plan apply, or a goal-data tweak) see the latest.
        currentGoal = currentGoal.copy(
            deadline = newDeadline,
            debtInterestRate = projection.scenario.annualRate * 100.0,
            debtRemainingMonths = months,
            debtPaymentFrequency = projection.scenario.paymentFrequency,
            debtMonthlyEquivalentPayment = projection.scenario.monthlyPayment,
        )

        appliedPlanKind = projection.scenario.kind
        renderStrip(panel)
        showUndoToast(panel, projection.scenario.kind)
    }

    private fun showUndoToast(panel: HTMLElement, kind: DebtScenarioKind) {
        val toast = panel.querySelector("#debtStudioToast") as? HTMLElement ?: return
        val tm = WebTheme.current
        val planLabel = when (kind) {
            DebtScenarioKind.PLAN_A -> StringKey.DEBT_STUDIO_PLAN_A.tr()
            DebtScenarioKind.PLAN_B -> StringKey.DEBT_STUDIO_PLAN_B.tr()
        }
        toast.innerHTML = """
            <span style="font-weight:500;">✓ ${StringKey.DEBT_STUDIO_APPLIED_TOAST.tr(planLabel)}</span>
            <button id="debt-undo-btn" style="
                background:${tm.accent}; color:#0a0a0a; border:none;
                font-size:13px; font-weight:700; cursor:pointer;
                padding:6px 14px; border-radius:6px;
                font-family:inherit; line-height:1;
            ">${StringKey.DEBT_STUDIO_UNDO.tr()}</button>
        """.trimIndent()
        toast.style.display = "flex"
        panel.querySelector("#debt-undo-btn")?.addEventListener("click", { undoApply(panel) })

        toastTimerId?.let { window.clearTimeout(it) }
        toastTimerId = window.setTimeout({
            toast.style.display = "none"
            undoSnapshot = null
            toastTimerId = null
        }, 2000)
    }

    private fun undoApply(panel: HTMLElement) {
        val snap = undoSnapshot ?: return
        viewModel.updateGoal(
            goal = currentGoal,
            name = snap.name,
            targetAmount = snap.targetAmount,
            deadline = JsDate("${snap.deadline.year}-${snap.deadline.monthNumber.toString().padStart(2, '0')}-${snap.deadline.dayOfMonth.toString().padStart(2, '0')}"),
            priority = snap.priority,
            debtInterestRate = snap.debtInterestRate,
            debtRemainingBalance = snap.debtRemainingBalance,
            debtRemainingMonths = snap.debtRemainingMonths,
            debtPaymentFrequency = snap.debtPaymentFrequency,
            debtMonthlyEquivalentPayment = snap.debtMonthlyEquivalentPayment,
        )
        currentGoal = snap
        appliedPlanKind = null
        renderStrip(panel)
        val toast = panel.querySelector("#debtStudioToast") as? HTMLElement
        toast?.style?.display = "none"
        toastTimerId?.let { window.clearTimeout(it) }
        toastTimerId = null
        undoSnapshot = null
    }

    private fun todayLocalDate(): LocalDate =
        kotlinx.datetime.Clock.System.todayIn(TimeZone.currentSystemDefault())

    // -----------------------------------------------------------------------
    // Formatters
    // -----------------------------------------------------------------------

    private fun formatSliderValue(v: Double, formatter: String): String = when (formatter) {
        "money" -> "\$${v.toLong()}"
        "percent" -> "${(v * 10000).toLong() / 100.0}%"
        else -> v.toString()
    }

    private fun parseSliderValue(text: String, formatter: String): Double? {
        val trimmed = text.trim()
        return when (formatter) {
            "money" -> trimmed.removePrefix("$").replace(",", "").trim().toDoubleOrNull()
            "percent" -> trimmed.removeSuffix("%").trim().toDoubleOrNull()?.let { it / 100.0 }
            else -> trimmed.toDoubleOrNull()
        }
    }

    private fun formatMoney(amount: Double): String {
        val rounded = kotlin.math.round(kotlin.math.abs(amount)).toLong()
        val withSeparators = rounded.toString().reversed().chunked(3).joinToString(",").reversed()
        return if (amount < 0) "-$withSeparators" else withSeparators
    }

    private fun formatShort(amount: Double): String {
        val abs = kotlin.math.abs(amount)
        return when {
            abs >= 1_000_000 -> "${(amount / 1_000_000.0 * 10).toLong() / 10.0}M"
            abs >= 1_000 -> "${(amount / 1_000.0).toLong()}k"
            else -> amount.toLong().toString()
        }
    }

    private fun formatMonthsAsYearsMonths(months: Int): String {
        val y = months / 12
        val m = months % 12
        return when {
            y > 0 && m > 0 -> "${y}y ${m}m"
            y > 0 -> "${y}y"
            else -> "${m}m"
        }
    }

    /** Delegates to the shared [HtmlEscape]; kept as a member so call sites read unchanged. */
    private fun escapeHtml(s: String): String = HtmlEscape.escape(s)
}
